import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArabicWord } from '../types';
import { getCurrentLanguage } from '../constants';
import { fetchWordBySlug } from '../services/wordService';
import WordCard from './WordCard';
import Navigation from './Navigation';

type PageState =
  | { status: 'loading' }
  | { status: 'word'; word: ArabicWord }
  | { status: 'notfound' };

// Normalise a raw URL param into the stored slug form (lowercase, no surrounding
// whitespace or hyphens). The slug itself is assigned server-side by
// cdk/scripts/assign-slugs.js.
const normaliseSlug = (raw: string | undefined): string =>
  (raw ?? '').trim().toLowerCase().replace(/^-+|-+$/g, '');

// Create or update a <meta>/<link> tag in <head>, returning a restore callback.
const upsertTag = (
  selector: string,
  create: () => HTMLElement,
  apply: (el: HTMLElement) => void
): (() => void) => {
  let el = document.head.querySelector<HTMLElement>(selector);
  const created = !el;
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  const previous = created ? null : (el.cloneNode(true) as HTMLElement);
  apply(el);
  return () => {
    if (!el) return;
    if (created) {
      el.remove();
    } else if (previous) {
      el.replaceWith(previous);
    }
  };
};

const WordPage: React.FC = () => {
  const params = useParams<{ slug: string }>();
  const slug = normaliseSlug(params.slug);
  const navigate = useNavigate();
  const [state, setState] = useState<PageState>({ status: 'loading' });
  const originalTitle = useRef<string>(document.title);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });

    if (!slug) {
      setState({ status: 'notfound' });
      return;
    }

    fetchWordBySlug(slug)
      .then((word) => {
        if (cancelled) return;
        setState(word ? { status: 'word', word } : { status: 'notfound' });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error resolving word slug:', err);
        setState({ status: 'notfound' });
      });

    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Send the browser to the canonical slug when the visited path differs
  // (casing, trailing slash, stray hyphens).
  useEffect(() => {
    if (state.status === 'word' && state.word.slug && slug !== state.word.slug) {
      navigate(`/${state.word.slug}`, { replace: true });
    }
  }, [state, slug, navigate]);

  // Per-word SEO tags; restored when leaving the page.
  useEffect(() => {
    if (state.status !== 'word') return;
    const { word } = state;
    const titleToRestore = originalTitle.current;
    const language = getCurrentLanguage();
    const canonicalSlug = word.slug || slug;
    const url = `${window.location.origin}/${canonicalSlug}`;
    const title = `${word.translation} — ${language} Word of the Day (${word.romanization})`;
    const description = word.example
      ? `${word.translation} in ${language}: ${word.word} (${word.romanization}). Example: ${word.example}`
      : `${word.translation} in ${language}: ${word.word} (${word.romanization}).`;

    document.title = title;

    const restores = [
      upsertTag(
        'link[rel="canonical"]',
        () => {
          const l = document.createElement('link');
          l.setAttribute('rel', 'canonical');
          return l;
        },
        (el) => el.setAttribute('href', url)
      ),
      upsertTag(
        'meta[name="description"]',
        () => {
          const m = document.createElement('meta');
          m.setAttribute('name', 'description');
          return m;
        },
        (el) => el.setAttribute('content', description)
      ),
    ];

    const ogPairs: Array<[string, string]> = [
      ['og:title', title],
      ['og:description', description],
      ['og:url', url],
      ['twitter:title', title],
      ['twitter:description', description],
      ['twitter:url', url],
    ];
    for (const [property, content] of ogPairs) {
      restores.push(
        upsertTag(
          `meta[property="${property}"]`,
          () => {
            const m = document.createElement('meta');
            m.setAttribute('property', property);
            return m;
          },
          (el) => el.setAttribute('content', content)
        )
      );
    }

    return () => {
      document.title = titleToRestore;
      for (const restore of restores) restore();
    };
  }, [state, slug]);

  // Tell crawlers not to index a missing word.
  useEffect(() => {
    if (state.status !== 'notfound') return;
    return upsertTag(
      'meta[name="robots"]',
      () => {
        const m = document.createElement('meta');
        m.setAttribute('name', 'robots');
        return m;
      },
      (el) => el.setAttribute('content', 'noindex, follow')
    );
  }, [state]);

  const playAudio = (): void => {
    if (state.status === 'word' && state.word.audioUrl) {
      new Audio(state.word.audioUrl).play();
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>{getCurrentLanguage()} Word of the Day</h1>
        <Navigation />
      </header>

      <main className="App-main">
        {state.status === 'loading' ? (
          <p>Loading...</p>
        ) : state.status === 'word' ? (
          <div className="word-container">
            <WordCard word={state.word} playAudio={playAudio} hideNavigation />
          </div>
        ) : (
          <div className="word-container">
            <h2>Word not found</h2>
            <p>
              We couldn&apos;t find that word. <a href="/">Go to today&apos;s word</a>.
            </p>
          </div>
        )}
      </main>

      <footer className="App-footer">
        <p>&copy; {new Date().getFullYear()} {getCurrentLanguage()} Word of the Day</p>
      </footer>
    </div>
  );
};

export default WordPage;
